/**
 * TypingIndicatorService
 *
 * Owns the peers' typing indicators in the conversation store. Every write
 * goes through here so each `typing` indicator has an expiry timer, restarted
 * whenever the indicator is replaced. When `peerExpiryMs` passes without an
 * update the person is dropped. This is what clears a typer whose client
 * disconnected without sending `stopped`, since the API emits no event when
 * its own typing state expires.
 *
 * `stopped` cancels the expiry. Its indicator stays in the store so `typing`
 * events older than the stop are still rejected, since delivery order is not
 * guaranteed.
 *
 * Timers run from when an update is applied locally, not from its server
 * timestamp, so clock skew between client and server cannot expire anyone early.
 */
import type { PeerExpiryMsResolver } from './index.types';
import type {
  ConversationTypingDTO,
  TypingIndicator,
  TypingIndicatorsForConversation,
} from '../../types/index.types';
import { selectTypingConfig } from 'Modules/config/store/index.selectors';
import { useConfigStore } from 'Modules/config/store/index.store';
import { useConversationStore } from 'Modules/conversation/store/index.store';

export * from './index.types';

const getPeerExpiryMsFromConfig: PeerExpiryMsResolver = () =>
  selectTypingConfig(useConfigStore.getState())?.peerExpiryMs ?? null;

/** Unambiguous because conversation ids are UUIDs and person ids are numeric. */
const getExpiryKey = (conversationId: string, personId: number) =>
  `${conversationId}:${personId}`;

export class TypingIndicatorService {
  private static instance: TypingIndicatorService | null = null;

  /** `conversationId:personId` → expiry timer */
  private readonly expiryTimers = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();

  static getInstance(): TypingIndicatorService {
    if (!TypingIndicatorService.instance) {
      TypingIndicatorService.instance = new TypingIndicatorService();
    }

    return TypingIndicatorService.instance;
  }

  /**
   * Applies a `ConversationTypingUpdated` event for one person.
   * `stopped` cancels the person's expiry. `typing` restarts it, and is
   * ignored while the typing config is unavailable, since it could not expire.
   */
  updateTypingIndicatorForPersonInConversation = (
    conversationId: string,
    personId: number,
    typingIndicator: TypingIndicator
  ): void => {
    if (typingIndicator.state === 'stopped') {
      const isApplied = useConversationStore
        .getState()
        .updateTypingIndicatorForPersonInConversation(
          conversationId,
          personId,
          typingIndicator
        );

      if (isApplied) {
        this.clearExpiry(conversationId, personId);
      }

      return;
    }

    const peerExpiryMs = getPeerExpiryMsFromConfig();

    if (peerExpiryMs === null) {
      return;
    }

    const isApplied = useConversationStore
      .getState()
      .updateTypingIndicatorForPersonInConversation(
        conversationId,
        personId,
        typingIndicator
      );

    if (isApplied) {
      this.scheduleExpiry(
        conversationId,
        personId,
        typingIndicator,
        peerExpiryMs
      );
    }
  };

  /**
   * Applies `typing` arrays returned by conversation fetches.
   * Ignored while the typing config is unavailable, since the indicators
   * could not expire.
   */
  applyFetchedTypingForConversations = (
    conversations: ConversationTypingDTO[]
  ): void => {
    const peerExpiryMs = getPeerExpiryMsFromConfig();

    if (peerExpiryMs === null) {
      return;
    }

    const previous = useConversationStore.getState().typingIndicators;
    useConversationStore
      .getState()
      .applyFetchedTypingForConversations(conversations);
    const next = useConversationStore.getState().typingIndicators;

    for (const { id: conversationId } of conversations) {
      if (!conversationId) {
        continue;
      }

      this.syncExpiryTimers(
        conversationId,
        previous.get(conversationId),
        next.get(conversationId),
        peerExpiryMs
      );
    }
  };

  /** Removes every indicator and cancels every pending expiry. */
  reset = (): void => {
    this.expiryTimers.forEach((timer) => clearTimeout(timer));
    this.expiryTimers.clear();
    useConversationStore.getState().clearTypingIndicators();
  };

  /**
   * A fetch can add, refresh and drop several people at once. The store
   * replaces an indicator object whenever it applies an update, so a changed
   * reference restarts the expiry, a removed person loses it, and an
   * untouched indicator keeps the expiry it already had.
   */
  private syncExpiryTimers(
    conversationId: string,
    previous: TypingIndicatorsForConversation | undefined,
    next: TypingIndicatorsForConversation | undefined,
    peerExpiryMs: number
  ): void {
    previous?.forEach((_, personId) => {
      if (!next?.has(personId)) {
        this.clearExpiry(conversationId, personId);
      }
    });

    next?.forEach((indicator, personId) => {
      if (previous?.get(personId) !== indicator) {
        this.scheduleExpiry(conversationId, personId, indicator, peerExpiryMs);
      }
    });
  }

  private scheduleExpiry(
    conversationId: string,
    personId: number,
    indicator: TypingIndicator,
    peerExpiryMs: number
  ): void {
    this.clearExpiry(conversationId, personId);

    const timer = setTimeout(() => {
      this.clearExpiry(conversationId, personId);
      useConversationStore
        .getState()
        .removeTypingIndicatorForPersonInConversation(
          conversationId,
          personId,
          indicator.lastUpdatedAt
        );
    }, peerExpiryMs);

    this.expiryTimers.set(getExpiryKey(conversationId, personId), timer);
  }

  private clearExpiry(conversationId: string, personId: number): void {
    const key = getExpiryKey(conversationId, personId);
    const timer = this.expiryTimers.get(key);

    if (timer === undefined) {
      return;
    }

    clearTimeout(timer);
    this.expiryTimers.delete(key);
  }
}
